/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { Checkbox, Grid, SelectOption, SilentSelectField, Typography2, breakpoints } from '@coursera/cds-core';
import { AddIcon, DownloadIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import GradebookExportDrawer from 'bundles/enterprise-admin-drawers/components/GradebookExportDrawer';
import RecentlyVisitedLearningProgramsBar from 'bundles/enterprise-admin-home/components/RecentlyVisitedLearningProgramsBar';
import type { TypeaheadV3Props } from 'bundles/enterprise-admin-home/components/TypeaheadV3';
import TypeaheadV3 from 'bundles/enterprise-admin-home/components/TypeaheadV3';
import { getShowResultsOptions, getSortByOptions } from 'bundles/enterprise-admin-home/utils/program-sort';
import type { ProgramOrder, ProgramSort } from 'bundles/enterprise-admin-home/utils/program-sort';
import EnterprisePermissionsRequired from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsRequired';
import { HowDoLearningProgramsWorkInformationCard } from 'bundles/enterprise-admin-skills/components/staticInformationCards';
import InformationCardToggle from 'bundles/enterprise-legacy-learner-home/components/InformationCard/InformationCardToggle';
import { Button, OverlayTrigger, Spacer, Tooltip, TrackedButton } from 'bundles/enterprise-ui';
import type { OrganizationType } from 'bundles/naptimejs/resources/__generated__/ThirdPartyOrganizationsV1';

import _t from 'i18n!nls/enterprise-admin-home';

const TrackedCheckbox = withSingleTracked({ type: 'CHECKBOX' })(Checkbox);

type PropsFromCaller = {
  withAbilityToShowProgramIds?: boolean;
  showProgramIds?: boolean;
  handleShowProgramIdsChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  programNameList: { id: string; name: string }[];
  withAbilityToAddProgram?: boolean;
  disableAddProgramButton?: boolean;
  onAddProgram: () => void;
  withAbilityToSearch?: boolean;
  thirdPartyOrganization: { id: string; organizationType?: OrganizationType | null; slug: string };
  searchProps?: TypeaheadV3Props;
  programSort: ProgramSort;
  programOrder: ProgramOrder;
  onProgramSortChange: (sort: ProgramSort) => void;
  onProgramOrderChange: (order: ProgramOrder) => void;
};

type Props = PropsFromCaller;

const styles = {
  actionRow: () => css`
    flex-direction: row;
    row-gap: var(--cds-spacing-200);
    column-gap: var(--cds-spacing-300);

    ${breakpoints.only('xs')} {
      flex-direction: 'column';
    }
  `,
  subheader: () => css`
    display: flex;
    justify-content: space-between;
    align-items: center;

    ${breakpoints.down('sm')} {
      flex-direction: column;
      align-items: flex-start;
    }
  `,
};

const ProgramListHeader = (props: Props) => {
  const track = useTracker();
  const { thirdPartySlug } = useParams();
  const [isInformationCardOpen, setIsInformationCardOpen] = React.useState(false);
  const [isGradebookExportDrawerOpen, setIsGradebookExportDrawerOpen] = React.useState<boolean>(false);
  const {
    withAbilityToShowProgramIds,
    showProgramIds,
    handleShowProgramIdsChange,
    withAbilityToAddProgram,
    disableAddProgramButton,
    onAddProgram,
    programNameList,
    withAbilityToSearch,
    thirdPartyOrganization,
    searchProps,
    programSort,
    programOrder,
    onProgramSortChange,
    onProgramOrderChange,
  } = props;
  const toggleInformationCard = () => {
    setIsInformationCardOpen(!isInformationCardOpen);
  };

  // Created the wrapper here instead of in ProgramSummaryListContainer because we can't use useTracker in a non-functional component
  // If ProgramSummaryListContainer ever gets changed to a functional component, we should move the track call directly into onAddProgram
  const handleAddProgram = () => {
    track('click_button', {
      button: {
        name: 'enterprise_create_program',
      },
      enterprise: {
        ...(thirdPartySlug ? { organizationSlug: thirdPartySlug } : {}),
      },
    });
    onAddProgram();
  };

  return (
    <div>
      <Typography2 variant="titleSmall" component="h2" color="body">
        {_t('Learning Programs')}
      </Typography2>
      <div css={styles.subheader}>
        <Typography2 component="p">
          {_t('Use learning programs as a way to organize courses or content and assign a group of learners to it.')}
        </Typography2>
        <InformationCardToggle
          isCdsEnabled
          isOpen={isInformationCardOpen}
          onClick={toggleInformationCard}
          label={_t('How learning programs work')}
          cardHtmlId="admin-dashboard-program-list-header"
          trackingName="how_learning_programs_work_information_link"
        />
      </div>
      {isInformationCardOpen && <Spacer size={16} />}
      <HowDoLearningProgramsWorkInformationCard
        id="admin-dashboard-program-list-header"
        isOpen={isInformationCardOpen}
        onRequestClose={toggleInformationCard}
      />
      <Spacer size={16} />
      <RecentlyVisitedLearningProgramsBar
        orgId={thirdPartyOrganization.id}
        orgSlug={thirdPartyOrganization.slug}
        shouldHideProgramsBar={programNameList.length < 5}
      />
      <Grid container alignItems="center" justifyContent="flex-end" css={styles.actionRow}>
        <EnterprisePermissionsRequired domain="ANY_PROGRAM" subdomain="GRADEBOOK" verb="EXPORT">
          <Grid item>
            <Button
              data-e2e="Export_Gradebook_Button"
              size="small"
              variant="ghost"
              onClick={() => setIsGradebookExportDrawerOpen(true)}
              icon={<DownloadIcon size="small" />}
              iconPosition="before"
            >
              {_t('Export gradebook')}
            </Button>
          </Grid>
        </EnterprisePermissionsRequired>
        <EnterprisePermissionsRequired
          domain="ORGANIZATION"
          domainId={thirdPartyOrganization.id}
          subdomain="PROGRAM"
          verb="CREATE"
          phaseOneOverride={withAbilityToAddProgram}
        >
          {withAbilityToAddProgram && (
            <Grid item>
              <OverlayTrigger
                placement="top"
                show={disableAddProgramButton ? undefined : false}
                overlay={
                  disableAddProgramButton ? (
                    <Tooltip>
                      {_t(
                        'You don’t have any active contracts. Please talk to your customer representative to add a new program.'
                      )}
                    </Tooltip>
                  ) : undefined
                }
              >
                <TrackedButton
                  trackingName="add_program_button"
                  trackingData={{ thirdPartyOrganizationId: thirdPartyOrganization.id }}
                  data-e2e="Add_Program_Button"
                  data-testid="create_new_learning_program_button"
                  variant="secondary"
                  size="small"
                  icon={<AddIcon size="small" />}
                  iconPosition="before"
                  disabled={disableAddProgramButton}
                  onClick={handleAddProgram}
                >
                  {_t('Create learning program')}
                </TrackedButton>
              </OverlayTrigger>
            </Grid>
          )}
        </EnterprisePermissionsRequired>
      </Grid>
      <Spacer size={24} />
      <Grid container spacing={24} alignItems="center">
        {withAbilityToSearch && (
          <Grid item css={{ flexGrow: 1 }}>
            <div css={{ maxWidth: 480 }}>
              <TypeaheadV3 searchUid="name" minSearchStrLength={1} searchIndexFields={['name']} {...searchProps} />
            </div>
          </Grid>
        )}
        {withAbilityToShowProgramIds && (
          <Grid item>
            <TrackedCheckbox
              label={_t('Show Program IDs')}
              trackingName="show_program_ids"
              trackingData={{ thirdPartyOrganizationId: thirdPartyOrganization.id }}
              onChange={handleShowProgramIdsChange}
              checked={showProgramIds}
            />
          </Grid>
        )}
        <Grid item css={{ marginBottom: 8 }}>
          <SilentSelectField
            labelPlacement="integrated"
            label={_t('Sort by')}
            value={programSort}
            onChange={(event) => onProgramSortChange(event.target.value as unknown as ProgramSort)}
          >
            {getSortByOptions().map(({ value, label }) => (
              <SelectOption value={value} key={value}>
                {label}
              </SelectOption>
            ))}
          </SilentSelectField>
        </Grid>
        <Grid item css={{ marginBottom: 8 }}>
          <SilentSelectField
            labelPlacement="integrated"
            label={_t('Show results')}
            value={programOrder}
            onChange={(event) => onProgramOrderChange(event.target.value as unknown as ProgramOrder)}
          >
            {getShowResultsOptions().map(({ value, label }) => (
              <SelectOption value={value} key={value}>
                {label}
              </SelectOption>
            ))}
          </SilentSelectField>
        </Grid>
      </Grid>
      {isGradebookExportDrawerOpen && programNameList && (
        <GradebookExportDrawer
          organizationId={thirdPartyOrganization.id}
          programs={programNameList}
          onClose={() => setIsGradebookExportDrawerOpen(false)}
        />
      )}
    </div>
  );
};

export default ProgramListHeader;
